import React, { Fragment } from 'react';
import { translate } from 'react-i18next';
import classnames from 'classnames';
import { Link } from 'react-router';

import Button from 'components/atoms/Button';
import ProgressBar from 'components/atoms/ProgressBar';
import MenuButton from 'components/atoms/MenuButton';
import MobileMenu from 'components/molecules/MobileMenu';
import Modal from 'components/molecules/Modal';
import Signup from 'components/molecules/Signup';

import { isEmpty } from 'util/helper';

import LogoIcon from 'images/icon/logo-black.inline.svg';
import LogoSmall from 'images/icon/logo-icon.inline.svg';

import styles from './index.css';

const Dropdown = ({ transparent, title, items, avatar = '', children }) => (
	<li
		className={classnames(styles.dropdown, {
			[styles.transparent]: transparent,
			[styles.avatar]: avatar !== '',
		})}
	>
		<div className={styles.title}>
			{avatar !== '' && (
				<div className={styles.avatar} style={{ backgroundImage: `url(${avatar})` }} />
			)}
			{title}
			<i className="material-icons">keyboard_arrow_down</i>
		</div>
		<ul>
			{items.map(item => (
				<li key={item.name}>
					{item.type === 'external' && (
						<a href={item.url} target="_blank">
							{item.name}
						</a>
					)}
					{item.type === 'internal' && <Link to={item.url}>{item.name}</Link>}
				</li>
			))}
			{children}
		</ul>
	</li>
);

const AuthSection = ({
	transparent,
	routeName,
	onOpenLoginSignupModal,
	isLogin,
	avatar,
	nickname,
	t,
	onLogout,
}) => {
	if (isLogin) {
		return (
			<Dropdown
				avatar={avatar}
				title={isEmpty(nickname) ? t('member') : nickname}
				transparent={transparent}
				items={[
					{ url: `/${routeName}/user/overview`, name: t('overview'), type: 'internal' },
					{ url: `/${routeName}/user/settings`, name: t('setting'), type: 'internal' },
					{ url: `/${routeName}/user/wishlist`, name: t('favorites'), type: 'internal' },
					{ url: `/${routeName}/user/reservation`, name: t('propertyHistory'), type: 'internal' },
					{
						url: `/${routeName}/user/booking`,
						name: t('reservationHistory'),
						type: 'internal',
					},
					{
						url: `/${routeName}/user/community`,
						name: t('onlineCommunity'),
						type: 'internal',
					},
				]}
			>
				<li>
					<button onClick={onLogout}>{t('logout')}</button>
				</li>
			</Dropdown>
		);
	}

	return (
		<Fragment>
			<li className={classnames({ [styles.transparent]: transparent })}>
				<button className={styles.title} onClick={() => onOpenLoginSignupModal('loginForm')}>
					{t('login')}
				</button>
			</li>
			<li className={classnames({ [styles.transparent]: transparent })}>
				<div>
					<Button
						color={transparent ? 'inverse' : ''}
						onClick={() => onOpenLoginSignupModal('signup')}
					>
						{t('register')}
					</Button>
				</div>
			</li>
		</Fragment>
	);
};

const NavigatorDesktop = ({
	transparent = false,
	loading = false,
	ratio,
	routeName,
	onOpenLoginSignupModal,
	isLogin,
	avatar,
	nickname,
	t,
	onLogout,
}) => (
	<nav
		className={classnames(styles.navigatorDesktop, {
			[styles.transparent]: transparent,
			[styles.englishWording]: routeName === 'en',
		})}
	>
		{!transparent && loading && <ProgressBar className={styles.loading} ratio={ratio} />}
		<Link to="/">
			<LogoIcon />
		</Link>
		<ul>
			<Dropdown
				title={t('choose')}
				transparent={transparent}
				items={[
					{ url: `/${routeName}/room`, name: t('filter'), type: 'internal' },
					{ url: `/${routeName}/map`, name: t('map'), type: 'internal' },
					{ url: 'https://pure.9floor.co/', name: t('pauian'), type: 'external' },
				]}
			/>
			<Dropdown
				title={t('ninthfloor')}
				transparent={transparent}
				items={[
					{ url: `/${routeName}/about`, name: t('about'), type: 'internal' },
					{ url: `/${routeName}/media`, name: t('media'), type: 'internal' },
					{ url: `/${routeName}/team`, name: t('team'), type: 'internal' },
					{ url: `/${routeName}/contact`, name: t('contact'), type: 'internal' },
				]}
			/>
			<Dropdown
				title={t('service')}
				transparent={transparent}
				items={[
					{ url: `/${routeName}/space`, name: t('space'), type: 'internal' },
					{ url: `/${routeName}/coliving`, name: t('coliving'), type: 'internal' },
				]}
			/>
			<li
				className={classnames(styles.dropdown, {
					[styles.transparent]: transparent,
				})}
			>
				<div className={styles.title}>
					<a target="_blank" rel="noopener noreferrer" href="https://medium.com/9floor-co-living">
						{t('journal')}
					</a>
				</div>
			</li>
			<AuthSection
				transparent={transparent}
				routeName={routeName}
				onOpenLoginSignupModal={onOpenLoginSignupModal}
				isLogin={isLogin}
				avatar={avatar}
				nickname={nickname}
				onLogout={onLogout}
				t={t}
			/>
		</ul>
	</nav>
);

const NavigatorMobile = ({
	open = false,
	transparent = false,
	loading = false,
	isLogin,
	nickname,
	ratio,
	media,
	toggleMenu,
	routeName,
	onOpenLoginSignupModal,
	t,
	onLogout,
	closeMenu,
}) => (
	<div>
		<nav
			className={classnames(styles.navigatorMobile, {
				[styles.transparent]: transparent,
				[styles.open]: open,
				[styles.englishWording]: routeName === 'en',
			})}
		>
			<MenuButton open={open} reverseColor={transparent} onClick={toggleMenu} />
			{!transparent && loading && <ProgressBar className={styles.loading} ratio={ratio} />}
			<Link to="/">{media === 'phone' ? <LogoSmall /> : <LogoIcon />}</Link>
			{!isLogin ? (
				<button
					onClick={() => {
						closeMenu();
						onOpenLoginSignupModal('loginForm');
					}}
				>
					{t('loginOrRegister')}
				</button>
			) : (
				<span>{isEmpty(nickname) ? t('member') : nickname}</span>
			)}
		</nav>
		<MobileMenu
			open={open}
			routeName={routeName}
			onOpenLoginSignupModal={onOpenLoginSignupModal}
			closeMenu={closeMenu}
			isLogin={isLogin}
			t={t}
			onLogout={onLogout}
		/>
	</div>
);

const Navigator = ({ media, routeName = 'zh-tw', ...other }) => (
	<Fragment>
		{media === 'desktop' ? (
			<NavigatorDesktop routeName={routeName} {...other} />
		) : (
			<NavigatorMobile media={media} routeName={routeName} {...other} />
		)}

		<Modal name="loginSignup">
			{({ message: { type, alert, success }, onDeactive }) => (
				<div className={styles.loginDialog}>
					<Signup type={type} alert={alert} success={success} />
					<button className={styles.closeButton} onClick={onDeactive}>
						<i className="material-icons">close</i>
					</button>
				</div>
			)}
		</Modal>
	</Fragment>
);

export default translate('navigator')(Navigator);



// WEBPACK FOOTER //
// ./src/components/molecules/Navigator/component.js